import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { getServices } from "../lib/servicesCache";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Clock, ArrowUpRight, Tag, X, MapPin, Sparkles, AlertCircle, ChevronLeft, Gift } from "lucide-react";
import BookingProgress from "../components/BookingProgress";

const TIME_SLOTS = ["10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00"];
const DRAFT_KEY = "tresses_booking_draft";

const readDraft = () => {
    try {
        const raw = sessionStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch { return null; }
};

export default function CheckoutPage() {
    const { user, refresh } = useAuth();
    const { settings } = useSettings();
    const minAmount = settings.min_booking_amount ?? 80;
    const leadMinutes = Math.max(0, parseInt(settings.min_booking_lead_minutes ?? 60, 10) || 0);
    const navigate = useNavigate();
    const draft = readDraft() || {};

    const [services, setServices] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [packagePromo, setPackagePromo] = useState(null);
    const [selectedIds] = useState(Array.isArray(draft.selectedIds) ? draft.selectedIds : []);
    const [selectedPackageIds] = useState(Array.isArray(draft.selectedPackageIds) ? draft.selectedPackageIds : []);
    const [packages, setPackages] = useState([]);
    const [variantSelections] = useState((draft.variantSelections && typeof draft.variantSelections === "object") ? draft.variantSelections : {});
    const [addonSelections] = useState((draft.addonSelections && typeof draft.addonSelections === "object") ? draft.addonSelections : {});
    // Today in LOCAL timezone (not UTC — toISOString would shift past-midnight users a day off).
    const todayLocal = (() => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${dd}`;
    })();
    // Auto-select today if user hasn't picked anything — so the summary reflects a valid date immediately.
    const [date, setDate] = useState(typeof draft.date === "string" && draft.date ? draft.date : todayLocal);
    const [time, setTime] = useState(typeof draft.time === "string" ? draft.time : "");
    const [notes, setNotes] = useState(typeof draft.notes === "string" ? draft.notes : "");
    const [coupon, setCoupon] = useState(typeof draft.coupon === "string" ? draft.coupon : "");
    const [pointsToUse, setPointsToUse] = useState(typeof draft.pointsToUse === "number" ? draft.pointsToUse : 0);
    const [creditsToUse, setCreditsToUse] = useState(typeof draft.creditsToUse === "number" ? draft.creditsToUse : 0);
    const [creditBalance, setCreditBalance] = useState(0);
    // When true, customer has opted out of using credits this booking (they want to save credits for later)
    const [skipCredits, setSkipCredits] = useState(typeof draft.skipCredits === "boolean" ? draft.skipCredits : false);
    // Reward mode: 'none' | 'coupon' | 'loyalty'
    // Initial mode is derived from any pre-existing draft so promo-attributed coupons land in 'coupon' mode.
    const initialRewardMode = (() => {
        if (typeof draft.coupon === "string" && draft.coupon) return "coupon";
        if (typeof draft.pointsToUse === "number" && draft.pointsToUse > 0) return "loyalty";
        return "none";
    })();
    const [rewardMode, setRewardMode] = useState(initialRewardMode);
    const [showConfirm, setShowConfirm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // Track whether the coupon arrived via a promo link (?coupon= → BookingPage → draft)
    // so we can render a celebratory banner around the auto-applied code.
    const autoAppliedCode = useMemo(() => {
        const c = typeof draft.coupon === "string" ? draft.coupon.trim().toUpperCase() : "";
        return c && draft.attribution_promotion_id ? c : "";
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Bounce back to /book if user came here without selecting anything
    useEffect(() => {
        if (selectedIds.length === 0 && selectedPackageIds.length === 0) {
            navigate("/book", { replace: true });
        }
    }, [selectedIds.length, selectedPackageIds.length, navigate]);

    useEffect(() => {
        getServices().then((data) => setServices(data));
        api.get("/coupons").then((r) => setCoupons(r.data));
        api.get("/packages", { params: { active_only: true } }).then((r) => setPackages(r.data || [])).catch(() => {});
        // Resolve package promo (if any) from the draft, in case user came here from a package link
        if (draft.promotion_id) {
            api.get("/promotions").then((r) => {
                const p = r.data.find((x) => x.id === draft.promotion_id && x.type === "package");
                if (p) setPackagePromo(p);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch live credit balance so the customer sees it at checkout.
    useEffect(() => {
        if (!user) { setCreditBalance(0); return; }
        api.get("/me/memberships")
            .then((r) => setCreditBalance(Number(r.data?.balance) || 0))
            .catch(() => setCreditBalance(0));
    }, [user?.id]);

    // Persist editable fields back into the draft on change
    useEffect(() => {
        try {
            const cur = readDraft() || {};
            sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
                ...cur,
                date, time, notes, coupon, pointsToUse, skipCredits,
                owner_email: user?.email ? user.email.toLowerCase() : (cur.owner_email || null),
            }));
            window.dispatchEvent(new Event("tresses-draft-changed"));
        } catch { /* ignore */ }
    }, [date, time, notes, coupon, pointsToUse, skipCredits, user?.email]);

    const selectedServices = useMemo(
        () => selectedIds.map((id) => services.find((s) => s.id === id)).filter(Boolean),
        [selectedIds, services]
    );
    const selectedPackages = useMemo(
        () => selectedPackageIds.map((id) => packages.find((p) => p.id === id)).filter(Boolean),
        [selectedPackageIds, packages]
    );
    const priceOf = (s) => {
        const v = (s.variants || []).find((x) => x.id === variantSelections[s.id]);
        const base = v ? Number(v.price) : Number(s.price);
        const chosen = addonSelections[s.id] || [];
        const addonSum = (s.addons || [])
            .filter((a) => chosen.includes(a.id))
            .reduce((sum, a) => sum + Number(a.price || 0), 0);
        return base + addonSum;
    };
    const chosenAddonsOf = (s) =>
        (s.addons || []).filter((a) => (addonSelections[s.id] || []).includes(a.id));
    const displayNameOf = (s) => {
        const v = (s.variants || []).find((x) => x.id === variantSelections[s.id]);
        return v ? `${s.name} \u2014 ${v.name}` : s.name;
    };
    const hasPackage = selectedPackages.length > 0;
    const packagesSubtotal = selectedPackages.reduce((sum, p) => sum + Number(p.price || 0), 0);
    const rawSubtotal = selectedServices.reduce((sum, s) => sum + priceOf(s), 0) + packagesSubtotal;
    const baseTotal = packagePromo ? Number(packagePromo.package_price || 0) : rawSubtotal;
    const totalDuration = selectedServices.reduce((sum, s) => sum + s.duration_min, 0) + selectedPackages.reduce((sum, p) => sum + (p.duration_min || 0), 0);
    // Packages are a fixed-price experience — no discount stacking allowed
    // (matches the backend guard, keeps the UI honest about the final price).
    const couponActive = !packagePromo && !hasPackage && rewardMode === "coupon" && !!coupon;
    const activeCoupon = couponActive ? coupons.find((c) => c.code === coupon) : null;
    const couponDiscount = activeCoupon ? Math.round(baseTotal * activeCoupon.discount_pct / 100) : 0;
    const afterCoupon = baseTotal - couponDiscount;

    const ptsToDollar = settings.loyalty_points_to_dollar || 10;
    const minRedeem = settings.loyalty_min_redeem || 100;
    const userPoints = user?.loyalty_points || 0;
    const loyaltyActive = !hasPackage && rewardMode === "loyalty";
    // Loyalty discount capped at 30% of the bill (admin policy)
    const LOYALTY_CAP_PCT = 0.30;
    const loyaltyMaxDollars = Math.floor(baseTotal * LOYALTY_CAP_PCT * 100) / 100;
    const loyaltyMaxPoints = Math.floor(loyaltyMaxDollars * ptsToDollar);
    const maxPointsUsable = loyaltyActive ? Math.min(userPoints, loyaltyMaxPoints) : 0;
    const safePoints = Math.min(Math.max(0, pointsToUse), maxPointsUsable);
    const loyaltyDiscount = safePoints / ptsToDollar;
    const priceAfterLoyalty = Math.max(0, afterCoupon - loyaltyDiscount);
    // Membership credits: auto-applied by default (customer's opt-in default) whenever they have a balance;
    // opts-out via `skipCredits`. Not combinable with a coupon.
    const creditsAllowed = !hasPackage && !couponActive && creditBalance > 0 && !skipCredits;
    const maxCreditsRedeemable = creditsAllowed ? Math.min(creditBalance, priceAfterLoyalty) : 0;
    const safeCredits = creditsAllowed ? Math.round(maxCreditsRedeemable * 100) / 100 : 0;
    const cashRemaining = creditsAllowed ? Math.max(0, priceAfterLoyalty - safeCredits) : priceAfterLoyalty;
    const finalPrice = Math.max(0, priceAfterLoyalty - safeCredits);

    // When a Special Package is added, forcibly clear any coupon/loyalty selection
    // and any residual reward-mode toggle so the customer doesn't see a phantom discount.
    useEffect(() => {
        if (hasPackage) {
            if (coupon) setCoupon("");
            if (pointsToUse) setPointsToUse(0);
            if (rewardMode !== "none") setRewardMode("none");
            if (creditsToUse) setCreditsToUse(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPackage]);

    // If admin disables the coupon programme mid-session, gracefully strip any coupon selection.
    useEffect(() => {
        if (settings.coupon_enabled === false) {
            if (rewardMode === "coupon") setRewardMode("none");
            if (coupon) setCoupon("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.coupon_enabled]);

    const switchRewardMode = (mode) => {
        if (mode === rewardMode) return;
        setRewardMode(mode);
        if (mode !== "coupon") setCoupon("");
        if (mode !== "loyalty") setPointsToUse(0);
    };

    const today = new Date().toISOString().split("T")[0];

    // `now` ticks every 30s so slot validity stays fresh while the page is open
    // (without it, opening the page at 09:30 and submitting "11:00" at 10:45 lets
    // an invalid same-day slot through to the API).
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(id);
    }, []);

    // Build the list of selectable time slots — if booking is for TODAY, drop slots that are
    // too soon (i.e. before now + minimum lead time configured by admin).
    const availableSlots = (() => {
        if (date !== todayLocal) return TIME_SLOTS;
        const cutoff = now.getHours() * 60 + now.getMinutes() + leadMinutes;
        return TIME_SLOTS.filter((slot) => {
            const [h, m] = slot.split(":").map(Number);
            return h * 60 + m >= cutoff;
        });
    })();
    // Visible value for the <select>: if the held `time` is no longer in the
    // filtered slots, render the placeholder instead of a stale value.
    const visibleTime = availableSlots.includes(time) ? time : "";
    // If current selection is no longer valid (e.g. user switched date to today and the chosen slot is past), drop it.
    useEffect(() => {
        if (time && !availableSlots.includes(time)) {
            setTime("");
        }
    }, [date, leadMinutes, time, availableSlots, now]);

    const dateOptions = useMemo(() => {
        const out = [];
        const base = new Date();
        for (let i = 0; i < 21; i++) {
            const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
            const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            out.push({ value, top: i === 0 ? "Today" : i === 1 ? "Tmrw" : d.toLocaleDateString("en-GB", { weekday: "short" }), day: d.getDate(), month: d.toLocaleDateString("en-GB", { month: "short" }) });
        }
        if (date && !out.some((o) => o.value === date) && date > todayLocal) {
            const [y, m, dd] = date.split("-").map(Number);
            const d = new Date(y, m - 1, dd);
            out.push({ value: date, top: d.toLocaleDateString("en-GB", { weekday: "short" }), day: dd, month: d.toLocaleDateString("en-GB", { month: "short" }) });
        }
        return out;
    }, [todayLocal, date]);
    const fmtSlot = (t) => {
        const [h, m] = t.split(":").map(Number);
        return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, "0")}` : ""} ${h < 12 ? "AM" : "PM"}`;
    };
    const todayHasSlots = (() => {
        const cutoff = now.getHours() * 60 + now.getMinutes() + leadMinutes;
        return TIME_SLOTS.some((slot) => { const [h, m] = slot.split(":").map(Number); return h * 60 + m >= cutoff; });
    })();
    // Jump to tomorrow automatically when no slots remain today (Today chip is disabled then)
    useEffect(() => {
        if (date === todayLocal && !todayHasSlots && dateOptions[1]) setDate(dateOptions[1].value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, todayHasSlots]);
    // Keep the selected date chip visible in the scroll strip
    useEffect(() => {
        document.querySelector(`[data-testid="date-chip-${date}"]`)?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }, [date]);
    // Past dates from an old draft reset to today
    useEffect(() => { if (date < todayLocal) setDate(todayLocal); }, [date, todayLocal]);

    const leadLabel = (() => {
        if (leadMinutes <= 0) return null;
        const h = Math.floor(leadMinutes / 60);
        const m = leadMinutes % 60;
        if (h && m) return `${h}h ${m}m`;
        if (h) return `${h} hour${h !== 1 ? "s" : ""}`;
        return `${m} minute${m !== 1 ? "s" : ""}`;
    })();

    const openConfirm = () => {
        if (!user) { toast.error("Please sign in to confirm"); navigate("/login"); return; }
        if (!date) { toast.error("Pick a date"); return; }
        if (date < todayLocal) { toast.error("Please pick a future date"); return; }
        if (!time) { toast.error("Pick a time"); return; }
        if (date === todayLocal && !availableSlots.includes(time)) {
            toast.error(leadLabel ? `Bookings need at least ${leadLabel} lead time — please pick a later slot` : "That time has passed — please pick a later slot");
            return;
        }
        if (!packagePromo && baseTotal < minAmount) { toast.error(`Minimum booking value is S$${minAmount}.`); return; }
        setShowConfirm(true);
    };

    const submitBooking = async () => {
        setSubmitting(true);
        let bookingData = null;
        let bookingErr = null;
        // 1) Place the booking — this is the only call whose failure means "booking failed".
        try {
            const { data } = await api.post("/bookings", {
                service_ids: selectedIds,
                package_ids: selectedPackageIds,
                date, time, notes,
                // Discounts are silently dropped when a Special Package is in the cart
                // (backend also enforces this — belt-and-braces).
                coupon_code: (packagePromo || hasPackage) ? null : (coupon || null),
                loyalty_points_redeem: hasPackage ? 0 : (safePoints || 0),
                credits_used: hasPackage ? 0 : (safeCredits || 0),
                promotion_id: packagePromo?.id || null,
                attribution_promotion_id: draft.attribution_promotion_id || packagePromo?.id || null,
                variant_selections: variantSelections,
                addon_selections: addonSelections,
            });
            bookingData = data;
        } catch (err) {
            bookingErr = err;
        }

        // 2) If the booking POST itself failed, surface the error and stop here.
        if (bookingErr) {
            toast.error(formatApiError(bookingErr.response?.data?.detail, "Booking failed"));
            setSubmitting(false);
            return;
        }

        // 3) Booking succeeded — from here on, NOTHING is allowed to throw and trap the user
        //    on a blank/half-rendered checkout page. Every side-effect is best-effort.
        try { toast.success("Booking placed — awaiting admin confirmation"); } catch { /* noop */ }
        try {
            if (bookingData?.unlocked_coupon?.code) {
                const c = bookingData.unlocked_coupon;
                toast.success(`🎁 Reward unlocked — ${c.discount_pct}% off your next visit. Code ${c.code} is now in your dashboard.`, { duration: 8000 });
            }
        } catch { /* noop */ }
        try { setShowConfirm(false); } catch { /* noop */ }
        try {
            sessionStorage.removeItem(DRAFT_KEY);
            window.dispatchEvent(new Event("tresses-draft-changed"));
        } catch { /* noop */ }
        // Refresh auth state but do NOT block navigation if it fails for any reason.
        try { await refresh?.(); } catch { /* noop */ }
        try { navigate("/dashboard"); } catch { /* noop */ }
        setSubmitting(false);
    };

    if (selectedIds.length === 0 && selectedPackageIds.length === 0) return null; // redirect effect will fire

    return (
        <div className="min-h-screen pt-32 pb-32 grain" data-testid="checkout-page">
            <div className="max-w-6xl mx-auto px-6 lg:px-12">
                <Link to="/book" className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] hover:text-[#8F622C] transition-colors" data-testid="back-to-services">
                    <ChevronLeft size={12} /> Back to services
                </Link>
                <div className="text-overline mt-4">Confirm your appointment</div>
                <h1 className="font-serif-display text-5xl sm:text-6xl text-[#3D0C1F] mt-3">Checkout.</h1>
                <p className="text-[#7A5262] mt-3 max-w-xl">Pick a date and time, apply any rewards, and we'll send your request to our team.</p>
                <BookingProgress step={2} />

                <div className="grid lg:grid-cols-3 gap-10 mt-12">
                    <div className="lg:col-span-2 space-y-10 min-w-0">
                        <section data-testid="date-time-section">
                            <h2 className="text-overline mb-4 flex items-center gap-2"><CalendarIcon size={12} /> 1. Choose a date</h2>
                            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1" data-testid="date-chips">
                                {dateOptions.map((d) => {
                                    const active = d.value === date;
                                    const off = d.value === todayLocal && !todayHasSlots;
                                    return (
                                        <button key={d.value} type="button" onClick={() => setDate(d.value)} disabled={off} title={off ? "No slots left today" : undefined}
                                            className={`shrink-0 w-[68px] py-3 rounded-2xl border text-center transition-colors disabled:opacity-35 disabled:cursor-not-allowed ${active ? "bg-[#6B1535] border-[#6B1535] text-[#FFF8F2]" : "bg-white border-[#C29A5B]/50 text-[#3D0C1F] hover:border-[#6B1535]"}`}
                                            data-testid={`date-chip-${d.value}`}>
                                            <span className={`block text-[0.6rem] tracking-[0.15em] uppercase ${active ? "text-[#F3DDA8]" : "text-[#8F622C]"}`}>{d.top}</span>
                                            <span className="block font-serif-display text-2xl leading-tight">{d.day}</span>
                                            <span className="block text-[0.6rem] uppercase tracking-wider opacity-80">{d.month}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <label className="inline-flex items-center gap-2 mt-2 text-xs text-[#6B1535] cursor-pointer">
                                Need a later date?
                                <input type="date" min={todayLocal} value={date} onChange={(e) => e.target.value && setDate(e.target.value)}
                                    className="bg-white border border-[#C29A5B]/50 rounded-full px-3 py-1 text-[#3D0C1F] outline-none focus:border-[#6B1535]"
                                    data-testid="booking-date" />
                            </label>

                            <h2 className="text-overline mt-8 mb-4 flex items-center gap-2"><Clock size={12} /> 2. Choose a time</h2>
                            {availableSlots.length > 0 ? (
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="time-chips">
                                    {availableSlots.map((t) => {
                                        const active = t === visibleTime;
                                        return (
                                            <button key={t} type="button" onClick={() => setTime(t)}
                                                className={`py-3 rounded-full border text-sm transition-colors ${active ? "bg-[#6B1535] border-[#6B1535] text-[#FFF8F2]" : "bg-white border-[#C29A5B]/50 text-[#3D0C1F] hover:border-[#6B1535]"}`}
                                                data-testid={`time-chip-${t.replace(":", "")}`}>
                                                {fmtSlot(t)}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-sm text-[#7A5262]" data-testid="no-slots">
                                    {leadLabel ? `No slots left today that meet the ${leadLabel} lead time — please pick another date.` : "No slots left on this day — please pick another date."}
                                </p>
                            )}
                            {date === todayLocal && availableSlots.length > 0 && leadLabel && (
                                <p className="text-[0.65rem] text-[#7A5262] mt-2">Same-day slots need at least {leadLabel} lead time.</p>
                            )}
                        </section>

                        <section>
                            <h2 className="text-overline mb-3">3. Notes (optional)</h2>
                            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                                placeholder="Any specific request, preferred stylist, allergies…"
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                data-testid="booking-notes" />
                        </section>
                    </div>

                    <aside className="card-luxe p-8 grain h-fit sticky top-28" data-testid="booking-summary">
                        <h3 className="font-serif-display text-2xl text-[#3D0C1F]">Summary</h3>
                        <div className="gold-divider my-5 opacity-60" />

                        <div className="space-y-3 mb-5">
                            {selectedPackages.map((p) => (
                                <div key={p.id} className="flex justify-between items-start gap-3 text-sm border-b border-[#C29A5B]/20 pb-2" data-testid={`summary-package-${p.id}`}>
                                    <div className="flex-1">
                                        <div className="text-[0.6rem] tracking-[0.2em] uppercase text-[#8F622C]">Package</div>
                                        <div className="text-[#3D0C1F]">{p.name}</div>
                                    </div>
                                    <div className="text-[#8F622C]">S${p.price}</div>
                                </div>
                            ))}
                            {selectedServices.map((s) => (
                                <div key={s.id} className="flex justify-between items-start gap-3 text-sm" data-testid={`summary-${s.id}`}>
                                    <div className="flex-1">
                                        <div className="text-[#3D0C1F]">{displayNameOf(s)}</div>
                                        {chosenAddonsOf(s).length > 0 && (
                                            <div className="text-[0.65rem] text-[#7A5262] mt-1 space-y-0.5">
                                                {chosenAddonsOf(s).map((a) => (
                                                    <div key={a.id} className="flex justify-between gap-3">
                                                        <span>+ {a.name}</span>
                                                        <span className="text-[#8F622C]">+S${a.price}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {settings.show_service_duration !== false && (
                                            <div className="text-xs text-[#8A6574] mt-0.5">{s.duration_min} min</div>
                                        )}
                                    </div>
                                    <div className="text-[#8F622C] whitespace-nowrap">S${priceOf(s)}</div>
                                </div>
                            ))}
                            <Link to="/book" className="block text-[0.6rem] tracking-[0.2em] uppercase text-[#7A5262] hover:text-[#8F622C] pt-1" data-testid="edit-services-link">Edit services →</Link>
                        </div>

                        <div className="flex justify-between text-sm">
                            <span className="text-[#7A5262]">Date</span>
                            <span className="text-[#3D0C1F]">{date ? new Date(`${date}T00:00:00`).toLocaleDateString("en-SG", { weekday: "short", day: "numeric", month: "short" }) : "—"}</span>
                        </div>
                        <div className="flex justify-between mt-2 text-sm">
                            <span className="text-[#7A5262]">Time</span>
                            <span className="text-[#3D0C1F]">{time ? fmtSlot(time) : "—"}</span>
                        </div>
                        {settings.show_service_duration !== false && (
                            <div className="flex justify-between mt-2 text-sm">
                                <span className="text-[#7A5262]">Total Duration</span>
                                <span className="text-[#3D0C1F]">{totalDuration} min</span>
                            </div>
                        )}

                        {packagePromo ? (
                            <div className="mt-6 p-4 border border-[#C29A5B]/50 bg-[#6B1535]/[0.06]" data-testid="package-banner">
                                <div className="text-overline flex items-center gap-2"><Sparkles size={12} className="text-[#8F622C]" /> Package applied</div>
                                <div className="font-serif-display text-lg text-[#3D0C1F] mt-1">{packagePromo.title}</div>
                                <p className="text-[0.7rem] text-[#8A6574] mt-1">Coupons cannot stack on packages.</p>
                            </div>
                        ) : hasPackage ? (
                            <div className="mt-6 p-4 border border-[#C29A5B]/30 bg-[#6B1535]/[0.05]" data-testid="package-no-discount-notice">
                                <div className="text-overline flex items-center gap-2"><Sparkles size={12} className="text-[#8F622C]" /> Fixed-price package</div>
                                <div className="font-serif-display text-lg text-[#3D0C1F] mt-1">{selectedPackages.length === 1 ? selectedPackages[0].name : `${selectedPackages.length} packages selected`}</div>
                                <p className="text-[0.7rem] text-[#8A6574] mt-1 leading-relaxed">
                                    Coupons and loyalty points cannot be applied when booking a Special Package. Remove the package to unlock rewards.
                                </p>
                            </div>
                        ) : (
                            <div className="mt-6" data-testid="rewards-block">
                                <div className="text-overline mb-2">Apply a reward</div>
                                <div role="radiogroup" aria-label="Choose reward type" className={`grid ${settings.coupon_enabled === false ? "grid-cols-2" : "grid-cols-3"} gap-1 p-1 border border-[#6B1535]/15 bg-[#FBF6F0]`}>
                                    {[
                                        { val: "none", label: "None" },
                                        { val: "coupon", label: "Coupon", hidden: settings.coupon_enabled === false },
                                        { val: "loyalty", label: "Loyalty", disabled: !user || settings.loyalty_enabled === false },
                                    ].filter((o) => !o.hidden).map((opt) => {
                                        const active = rewardMode === opt.val;
                                        return (
                                            <button
                                                key={opt.val}
                                                type="button"
                                                role="radio"
                                                aria-checked={active}
                                                disabled={opt.disabled}
                                                onClick={() => !opt.disabled && switchRewardMode(opt.val)}
                                                data-testid={`reward-mode-${opt.val}`}
                                                className={
                                                    "py-2 text-[0.65rem] tracking-[0.22em] uppercase transition-colors " +
                                                    (active
                                                        ? "bg-[#6B1535] text-[#FFF8F2]"
                                                        : opt.disabled
                                                            ? "text-[#444] cursor-not-allowed"
                                                            : "text-[#7A5262] hover:text-[#3D0C1F] hover:bg-[#6B1535]/[0.04]")
                                                }
                                            >
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>

                                {rewardMode === "coupon" && (
                                    <>
                                        <label className="text-overline mt-5 flex items-center gap-2"><Tag size={12} /> Coupon code</label>
                                        {(() => {
                                            const showWelcome = !!(autoAppliedCode && coupon && coupon.trim().toUpperCase() === autoAppliedCode && activeCoupon && couponDiscount > 0);
                                            const inputBorder = showWelcome
                                                ? "border-[#C29A5B] focus:border-[#C29A5B] shadow-[0_0_0_1px_rgba(212,175,55,0.35)]"
                                                : "border-[#6B1535]/15 focus:border-[#C29A5B]";
                                            return (
                                                <>
                                                    {showWelcome && (
                                                        <div
                                                            className="mt-3 px-4 py-3 border border-[#C29A5B]/55 bg-gradient-to-r from-[#C29A5B]/[0.10] to-[#C29A5B]/[0.04] grain flex items-center gap-3 animate-in fade-in slide-in-from-top-1 duration-500"
                                                            data-testid="welcome-coupon-banner"
                                                        >
                                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#6B1535]/15 border border-[#C29A5B]/40 flex items-center justify-center">
                                                                <Gift size={14} className="text-[#8F622C]" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[#8F622C]">Welcome offer applied</div>
                                                                <div className="text-[0.78rem] text-[#3D0C1F] mt-0.5">
                                                                    You&apos;re saving <span className="font-semibold text-[#8F622C]">S${couponDiscount}</span> today with <span className="font-semibold">{autoAppliedCode}</span>
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setCoupon("")}
                                                                className="text-[#7A5262] hover:text-[#8F622C] p-1 flex-shrink-0"
                                                                aria-label="Remove coupon"
                                                                data-testid="welcome-coupon-remove"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    )}
                                                    <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                                                        list="coupon-list" placeholder="WELCOME20"
                                                        className={`w-full bg-[#FBF6F0] border ${inputBorder} px-3 py-2 mt-2 outline-none text-sm text-[#3D0C1F] transition-colors`}
                                                        data-testid="booking-coupon" />
                                                    <datalist id="coupon-list">
                                                        {coupons.map((c) => <option key={c.code} value={c.code}>{c.description}</option>)}
                                                    </datalist>
                                                </>
                                            );
                                        })()}
                                    </>
                                )}

                                {rewardMode === "loyalty" && user && settings.loyalty_enabled !== false && (
                                    <div className="mt-5 p-4 border border-[#C29A5B]/25 bg-[#6B1535]/[0.04]" data-testid="loyalty-redeem-block">
                                        <div className="flex items-center justify-between">
                                            <div className="text-overline flex items-center gap-2"><Sparkles size={12} /> Loyalty</div>
                                            <div className="text-[0.65rem] text-[#7A5262]">{userPoints} pts available</div>
                                        </div>
                                        <p className="text-[0.7rem] text-[#8A6574] mt-2">{ptsToDollar} pts = S$1 · Min {minRedeem} pts · Max 30% of bill</p>
                                        {userPoints < minRedeem ? (
                                            <p className="text-xs text-[#7A5262] mt-3" data-testid="loyalty-not-enough">
                                                Earn {minRedeem - userPoints} more pts to redeem on your next booking.
                                            </p>
                                        ) : afterCoupon < 1 ? (
                                            <p className="text-xs text-[#7A5262] mt-3">Add services to redeem points.</p>
                                        ) : maxPointsUsable < minRedeem ? (
                                            <p className="text-xs text-[#7A5262] mt-3">
                                                This bill is too small to redeem the minimum {minRedeem} pts (max 30% allowed).
                                            </p>
                                        ) : (
                                            <>
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={Math.floor(maxPointsUsable)}
                                                    step={ptsToDollar}
                                                    value={safePoints}
                                                    onChange={(e) => setPointsToUse(parseInt(e.target.value, 10))}
                                                    className="w-full mt-3 accent-[#6B1535]"
                                                    data-testid="loyalty-slider"
                                                />
                                                <div className="flex justify-between text-xs mt-2">
                                                    <button type="button" onClick={() => setPointsToUse(0)} className="text-[#7A5262] hover:text-[#8F622C]" data-testid="loyalty-clear">Clear</button>
                                                    <button type="button" onClick={() => setPointsToUse(Math.floor(maxPointsUsable))} className="text-[#7A5262] hover:text-[#8F622C]" data-testid="loyalty-max">Use max ({Math.floor(maxPointsUsable)} pts)</button>
                                                </div>
                                                {safePoints >= minRedeem && (
                                                    <div className="mt-3 text-xs flex items-center justify-between">
                                                        <span className="text-[#3D0C1F]">Using <span className="gold-text font-medium">{safePoints}</span> pts</span>
                                                        <span className="gold-text">−S${loyaltyDiscount.toFixed(2)}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                {rewardMode === "none" && (
                                    <p className="text-xs text-[#7A5262] mt-3">Choose a reward type to save on this booking. <span className="text-[#8A6574]">You can only apply one per booking.</span></p>
                                )}
                            </div>
                        )}

                        {/* Membership credits — auto-applied when user has a balance and hasn't opted out */}
                        {user && creditBalance > 0 && !hasPackage && !couponActive && (
                            <div className="mt-5 border border-[#C29A5B]/20 p-4 bg-[#F7E4DE]" data-testid="checkout-credits-section">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#8F622C]">Membership Credits</div>
                                        <div className="text-[0.7rem] text-[#7A5262] mt-1">Balance <span className="text-[#3D0C1F] font-medium">S${creditBalance.toFixed(2)}</span></div>
                                    </div>
                                    <label className="flex items-center gap-2 text-[0.65rem] text-[#7A5262] cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={skipCredits}
                                            onChange={(e) => setSkipCredits(e.target.checked)}
                                            className="accent-[#6B1535]"
                                            data-testid="checkout-skip-credits-toggle"
                                        />
                                        <span>Don't use credits this time</span>
                                    </label>
                                </div>

                                {!skipCredits ? (
                                    <div className="mt-3 space-y-2" data-testid="checkout-credits-auto-applied">
                                        <div className="flex items-baseline justify-between">
                                            <span className="text-[0.7rem] text-[#7A5262]">Applied to this booking</span>
                                            <span className="font-serif-display text-lg gold-text">−S${safeCredits.toFixed(2)}</span>
                                        </div>
                                        {cashRemaining > 0.001 ? (
                                            <div className="mt-2 border-t border-[#6B1535]/10 pt-3">
                                                <div className="flex items-baseline justify-between">
                                                    <span className="text-[0.7rem] tracking-[0.2em] uppercase text-[#3D0C1F]">Due in cash at salon</span>
                                                    <span className="font-serif-display text-lg text-[#3D0C1F]">S${cashRemaining.toFixed(2)}</span>
                                                </div>
                                                <p className="text-[0.65rem] text-[#8A6574] mt-2" data-testid="checkout-credits-cash-message">
                                                    Your credits cover part of this booking. Pay the remaining <span className="text-[#3D0C1F]">S${cashRemaining.toFixed(2)}</span> in cash at the salon — or <Link to="/membership" className="text-[#8F622C] hover:underline">top up your membership →</Link>
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="text-[0.65rem] text-[#8A6574] mt-1" data-testid="checkout-credits-full-cover-message">Fully covered by your membership credits. No cash due at the salon.</p>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-[0.65rem] text-[#8A6574] mt-3">Credits will be saved for a future booking. Uncheck the box above to apply them here.</p>
                                )}
                            </div>
                        )}

                        <div className="gold-divider my-5 opacity-60" />
                        <div className="flex justify-between text-sm">
                            <span className="text-[#7A5262]">{packagePromo ? "Package price" : "Subtotal"}</span>
                            <span className="text-[#3D0C1F]">S${baseTotal}</span>
                        </div>
                        {couponDiscount > 0 && (
                            <div className="flex justify-between text-sm mt-1">
                                <span className="text-[#7A5262]">Coupon ({activeCoupon.discount_pct}%)</span>
                                <span className="text-[#8F622C]">-S${couponDiscount}</span>
                            </div>
                        )}
                        {safePoints >= minRedeem && (
                            <div className="flex justify-between text-sm mt-1" data-testid="summary-loyalty-line">
                                <span className="text-[#7A5262]">Loyalty ({safePoints} pts)</span>
                                <span className="text-[#8F622C]">-S${loyaltyDiscount.toFixed(2)}</span>
                            </div>
                        )}
                        {safeCredits > 0 && (
                            <div className="flex justify-between text-sm mt-1" data-testid="summary-credits-line">
                                <span className="text-[#7A5262]">Membership credits</span>
                                <span className="text-[#8F622C]">-S${safeCredits.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between mt-4 text-base">
                            <span className="text-overline">Total</span>
                            <span className="font-serif-display text-2xl gold-text">S${finalPrice.toFixed(2)}</span>
                        </div>

                        <button onClick={openConfirm} disabled={submitting || (!packagePromo && baseTotal < minAmount) || !date || !time}
                            className="btn-gold w-full mt-6 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="booking-confirm">
                            {submitting ? "Reserving…" : "Confirm Booking"} <ArrowUpRight size={14} />
                        </button>
                    </aside>
                </div>
            </div>

            {/* Confirmation modal */}
            {showConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#3D0C1F]/45 backdrop-blur-sm px-4" data-testid="booking-confirm-modal">
                    <div className="relative max-w-lg w-full bg-[#FDF0EC] border border-[#C29A5B]/40 grain p-8 max-h-[90vh] overflow-y-auto">
                        <button onClick={() => setShowConfirm(false)} className="absolute top-4 right-4 text-[#7A5262] hover:text-[#8F622C]" data-testid="confirm-close"><X size={20} /></button>
                        <div className="text-overline">Please confirm</div>
                        <h3 className="font-serif-display text-3xl text-[#3D0C1F] mt-3">Is everything <span className="italic gold-text">just right?</span></h3>
                        <p className="text-sm text-[#7A5262] mt-3">Please verify the details below before we send your booking to our team.</p>
                        <div className="gold-divider my-6 opacity-60" />
                        <div className="space-y-5">
                            <div>
                                <div className="text-overline mb-2 flex items-center gap-2"><CalendarIcon size={12} /> Date & Time</div>
                                <div className="font-serif-display text-2xl text-[#3D0C1F]" data-testid="confirm-datetime">
                                    {new Date(date).toLocaleDateString("en-SG", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {time ? fmtSlot(time) : ""}
                                </div>
                                {settings.show_service_duration !== false && (
                                    <div className="text-xs text-[#7A5262] mt-1">Total duration: {totalDuration} minutes</div>
                                )}
                            </div>
                            <div>
                                <div className="text-overline mb-2 flex items-center gap-2"><MapPin size={12} /> Your Address</div>
                                {(user?.address_line || user?.city) ? (
                                    <>
                                        <div className="text-sm text-[#3D0C1F]" data-testid="confirm-address">
                                            {[user?.address_line, user?.city, user?.postal_code, user?.country].filter(Boolean).join(", ")}
                                        </div>
                                        <div className="text-xs text-[#7A5262] mt-1">{user?.phone || ""}</div>
                                        <Link to="/dashboard" className="text-[0.65rem] tracking-[0.18em] uppercase text-[#7A5262] hover:text-[#8F622C] mt-2 inline-flex items-center gap-1" data-testid="confirm-address-edit">Edit on profile</Link>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-sm text-amber-700" data-testid="confirm-address-missing">No address on profile.</div>
                                        <Link to="/dashboard" className="text-[0.65rem] tracking-[0.18em] uppercase text-[#8F622C] hover:underline mt-2 inline-flex items-center gap-1">Add your address →</Link>
                                    </>
                                )}
                            </div>
                            <div>
                                <div className="text-overline mb-2">Services ({selectedServices.length})</div>
                                <ul className="space-y-2">
                                    {selectedServices.map((s) => (
                                        <li key={s.id} className="flex justify-between text-sm" data-testid={`confirm-service-${s.id}`}>
                                            <span className="text-[#3D0C1F]">{displayNameOf(s)}</span>
                                            <span className="text-[#8F622C]">S${priceOf(s)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="pt-4 border-t border-[#6B1535]/15">
                                <div className="flex justify-between text-sm">
                                    <span className="text-[#7A5262]">{packagePromo ? "Package price" : "Subtotal"}</span>
                                    <span className="text-[#3D0C1F]">S${baseTotal}</span>
                                </div>
                                {couponDiscount > 0 && (
                                    <div className="flex justify-between text-sm mt-1">
                                        <span className="text-[#7A5262]">Coupon ({activeCoupon.discount_pct}%)</span>
                                        <span className="text-[#8F622C]">-S${couponDiscount}</span>
                                    </div>
                                )}
                                {safePoints >= minRedeem && (
                                    <div className="flex justify-between text-sm mt-1" data-testid="confirm-loyalty-line">
                                        <span className="text-[#7A5262]">Loyalty ({safePoints} pts)</span>
                                        <span className="text-[#8F622C]">-S${loyaltyDiscount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between mt-3">
                                    <span className="text-overline">Total Payable</span>
                                    <span className="font-serif-display text-3xl gold-text">S${finalPrice.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="gold-divider my-6 opacity-60" />
                        <p className="text-[0.7rem] text-[#7A5262] mb-5 leading-relaxed">
                            By confirming, you agree to attend at the time and address above. Our team will email/call to finalise once approved.
                        </p>
                        <div className="flex gap-3">
                            <button onClick={() => setShowConfirm(false)} className="btn-outline-gold flex-1 justify-center" data-testid="confirm-edit">
                                Edit Details
                            </button>
                            <button onClick={submitBooking} disabled={submitting} className="btn-gold flex-1 justify-center disabled:opacity-50" data-testid="confirm-final">
                                {submitting ? "Booking…" : "Yes, Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
